import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { Button, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TextFieldWrapper from '../../micro/TextFieldWrapper/TextFieldWrapper';
import { loginAPIcall, setLoginError } from '../../../redux/actions/auth/auth.actions';
// import RequestAccessModal from '../../micro/RequestAccessModal/RequestAccessModal';
import HomeNavBar from '../../micro/HomeNavBar/HomeNavBar';
import HomeFooter from '../../micro/HomeFooter/HomeFooter';
import { BASE_URL, IMAGE_PATHS } from '../../../constants/paths.constants';
import { lightGrey } from '../../../app/theme';
import AppState from '../../../models/redux/state/app.state';
import history from '../../../services/browserHistory';

const useStyles = makeStyles((theme: Theme) => ({
  textLinkButtons: {
    borderRadius: 0,
    height: '1em',
    padding: 0,
    marginLeft: '0.25em',
    textTransform: 'none',
    color: theme.palette.secondary.main,
    fontWeight: 'bold',
    fontSize: 16,
    textDecoration: 'underline',
    verticalAlign: 'baseline',
  },  
  images: {
    display: 'block',
    width: '100%',
  },
  bedLogo: {
    paddingLeft: 65,
    paddingTop: 16,
    width: 239,
    height: 63,
  },
  doctorImage: {
    backgroundImage: `url(${BASE_URL}${IMAGE_PATHS.PHYSICIANS_CHATTING.path})`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    borderRadius: '0 16px 16px 0',
    width: '45%',
    height: 578,
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    height: '100vh',
  },
  signInContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '3em 0',
  },
  signInForm: {
    width: '45%',
    marginRight: '4em',
  },
  signInTitle: {
    marginBottom: '1.5em',
    paddingBottom: '0.5em',
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
    color: theme.palette.secondary.main,
    fontSize: '2em',
    fontWeight: 600,
  },
  signInButton: {
    textTransform: 'uppercase',
    fontWeight: 600,
    borderRadius: 1000,
    padding: '0.5em 2em',
  },
  fieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: 475,
    margin: '2em 0',
  },
  fieldsInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  requestAccess: {
    margin: '1em 0',
    display: 'flex',
    alignItems: 'baseline',
    fontSize: 16,
  },
  inputField: {
    color: lightGrey,
    textTransform: 'uppercase',
  },
  forgotPasswordButton: {
    textTransform: 'none',
    textDecoration: 'underline',
    fontStyle: 'italic',
    fontSize: 16,
    padding: 0,
    margin: 0,
  },
  error: {
    marginTop: 10,
    color: 'red',
    fontSize: 16,
    lineHeight: 1.2,
    width: 'calc(100% + 100px)',
  },
}));

const loginSchema = Yup.object({
  email: Yup.string().required('*Required'),
  password: Yup.string().required('*Required'),
});

const Login = (): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const errorMessage = useSelector((state: AppState) => state.auth.errorMessage);

  useEffect(() => {
    return () => {
      dispatch(setLoginError(null));
    };
  }, [dispatch]);

  const handleRegisterRedirect = () => {
    history.push('/user-registration');
  };

  return (
    <div className={classes.container}>
      <div>
        <HomeNavBar />
        <img
          className={classes.bedLogo}
          src={`${BASE_URL}${IMAGE_PATHS.BED_LOGO.path}`}
          alt={`${IMAGE_PATHS.BED_LOGO.alt}`}
        />
      </div>
      <div className={classes.signInContainer}>
        <div className={classes.doctorImage} />
        <div className={classes.signInForm}>
          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={loginSchema}
            onSubmit={(values) => {
              dispatch(setLoginError(null));
              dispatch(loginAPIcall(values));
            }}
          >
            {(formProps) => (
              <form onSubmit={formProps.handleSubmit}>
                <Typography variant="h1" className={classes.signInTitle}>
                  Sign In
                </Typography>
                <div className={classes.fieldsContainer}>
                  <TextFieldWrapper
                    form={formProps}
                    formKey="email"
                    label="Email*"
                    variant="outlined"
                    inputLabelProps={{ className: classes.inputField }}
                  />
                  <TextFieldWrapper
                    form={formProps}
                    formKey="password"
                    label="Password*"
                    type="password"
                    variant="outlined"
                    inputLabelProps={{ className: classes.inputField }}
                  />
                  <div className={classes.fieldsInfo}>
                    <Link to="../reset">
                      <Button className={classes.forgotPasswordButton} size="small" color="secondary">
                        Forgot Password?
                      </Button>
                    </Link>
                    <Typography>*Required field</Typography>
                  </div>
                  {errorMessage && <Typography className={classes.error}>{errorMessage}</Typography>}
                </div>
                <Button className={classes.signInButton} variant="contained" color="secondary" type="submit">
                  Sign In
                </Button>
                <div className={classes.requestAccess}>
                  <span>
                    Don&apos;t have an account?
                    <Button
                      size="small"
                      className={classes.textLinkButtons}
                      onClick={() => handleRegisterRedirect()}
                    >
                      Register
                    </Button>
                    .
                  </span>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
      <div>
        <img
          className={classes.images}
          src={`${BASE_URL}${IMAGE_PATHS.TRICOLOR_BAR.path}`}
          alt={`${IMAGE_PATHS.TRICOLOR_BAR.alt}`}
        />
        <HomeFooter />
      </div>
    </div>
  );
};

export default Login;
